import Summary from '@/modules/common/hiring/models/APP/Summary'
import { format } from '@/utils/date.utils'
import { addCommas } from '@/utils/textUtils'
import { useForeignHealthStore } from '../../stores'
import type {
  SummaryType3Interface,
  SummaryType2Interface,
  SummaryType1Interface
} from '@/modules/core/models/types'
import { UserRoleEnum } from '@/modules/core/models/enums'
import { priceFormat } from '@/utils/textUtils'
import type { PricePeriodType } from '@/modules/common/common/models/APP/types'
import type ForeignHealthHiringAnswers from './ForeignHealthHiringAnswers'
import { CURRENCY } from '@/modules/common/common/constants/CommonConstants'
import { useSummaryComposable } from '@/modules/core/composables/useSummaryComposable'

export default class HealthHiringSummary implements Summary {
  private answers: ForeignHealthHiringAnswers
  private t
  private foreignHealthStore
  private summaryComposable

  constructor(data: ForeignHealthHiringAnswers, t: (key: string, values?: any) => string) {
    this.answers = data
    this.t = t
    this.foreignHealthStore = useForeignHealthStore()
    this.summaryComposable = useSummaryComposable(t)
  }

  private getInsuredLabel(role: string): string {
    if (role === UserRoleEnum.HOLDER_INSURED) {
      return 'holder-insured'
    }
    if (role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return 'holder'
    }
    return 'insured'
  }

  private getIndex(role: string, index: number, holderIsNotInsured: boolean): number {
    if (holderIsNotInsured && role === UserRoleEnum.INSURED) {
      return index
    }
    return index + 1
  }

  #getStep0Summary(): SummaryType1Interface[] {
    const postalCodes = this.answers.insureds.map((i) => i.addressDetails.postalCode).join(', ')
    const birthDates = this.answers.insureds.map(({ userDetails }) => format(userDetails.birthDate))

    return [
      {
        label: this.t(`copy_summary_step0_date_label`),
        value: format(this.answers.startDate),
        visible: !!this.answers.startDate
      },
      {
        label: this.t('copy_summary_step0_end_date_label'),
        value: format(this.answers.endDate),
        visible: !!this.answers.endDate
      },
      {
        label: this.t(`copy_summary_step0_postal-codes_label`),
        value: postalCodes === ', ' ? '' : postalCodes,
        visible: postalCodes !== ', '
      },
      // {
      //   label: this.t(`copy_summary_step0_billing_label`),
      //   value: this.answers.billingCycle
      //     ? CommonMapper.toDXPProductPrice(this.answers.billingCycle)
      //     : '',
      //   visible: !!this.answers.billingCycle
      // },
      {
        label: this.t(`copy_summary_step0_num-insureds_label`),
        value: this.t('components.InsuredSubheaderComponent.label', this.answers.insureds.length),
        visible: !!this.answers.insureds.length
      },
      {
        label: this.t(`copy_summary_step0_birth-dates_label`, birthDates.length),
        value: birthDates.length ? birthDates.join(', ') : '',
        visible: !!birthDates.length
      }
    ]
  }

  #getStep1Summary(): SummaryType2Interface[] {
    const coverages = this.foreignHealthStore.getAdditionalCoveragesConfig()
    const hideIndex = this.answers.insureds.length === 1 && this.answers.insureds[0].isHolder
    return this.answers.insureds.map((item, i) => {
      const i18nLabel = item.isHolder ? 'holder-insured' : 'insured'
      const labelInsured = this.t(`hiring.common.${i18nLabel}`, hideIndex ? null : i + 1)
      const labelDate = format(item.userDetails.birthDate)

      const values = item.additionalCoverages.reduce((acc: string[], coverage) => {
        if (!coverage.checked) {
          return acc
        }

        const label = coverages.find((c) => c.code === coverage?.key)?.label
        const value = coverage.value ? ` - ${addCommas(String(coverage.value))}${CURRENCY}` : ''

        return [...acc, `${label ?? '-- --'}${value}`]
      }, [])

      return {
        label: `${labelInsured} (${labelDate})`,
        visible: !!item.userDetails.birthDate && !!values.length,
        values
      }
    })
  }

  #getInsuredsData(subStepIndex: number): SummaryType3Interface[] {
    const insureds = [...this.answers.insureds]
    let holderIsNotInsured = false
    if (this.answers.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      insureds.unshift(this.answers.holder)
      holderIsNotInsured = true
    }

    return insureds.map((insured, i) => {
      const { userDetails, role } = insured

      const i18nLabel = this.getInsuredLabel(role)
      const labelInsured = this.t(
        `hiring.common.${i18nLabel}`,
        this.getIndex(role, i, holderIsNotInsured)
      )
      const label = userDetails.name ? userDetails.getFullName() : ''
      const page = `Step2_${i}_1_InsuranceAnotherCompanyForm`

      const subStep1 = this.summaryComposable.getDocumentInsuredStep(insured)
      const subStep2 = this.summaryComposable.getAddressStep(insured)
      const subStep3 = [
        this.summaryComposable.getEmailStep(insured),
        this.summaryComposable.getPhoneStep(insured)
      ]
      const subStep4 = [
        {
          label: this.t('hiring.summary.foreign-health.step2.study'),
          visible: insured.studentSituation === 'S' || insured.studentSituation === 'N',
          value:
            insured.studentSituation === 'S'
              ? this.t('common.choices.yes')
              : this.t('common.choices.no')
        },
        {
          label: this.t('hiring.summary.foreign-health.step2.probe'),
          visible: !!insured.extraDocument?.fileName,
          value: insured.extraDocument?.fileName
        }
      ]
      const stepsToFilter = [subStep1, subStep2, subStep3]
      const currentRiskNumber = this.answers.currentInsured?.riskNumber
      const subSteps = this.summaryComposable.filterInsuredsRenderableSteps(
        insured,
        currentRiskNumber!,
        stepsToFilter,
        subStepIndex
      )

      const allSteps = [...subSteps, ...subStep4]
      return {
        label: label,
        visible: subSteps.length > 0 && !!label,
        page,
        subLabel: labelInsured,
        values: allSteps
      }
    })
  }

  #getStep3Summary(): SummaryType3Interface[] {
    const i18nRoot = 'hiring.summary.health.step3'

    const bodyMeasureVisible = this.answers.insureds.every(
      (i) => i.userDetails.weight && i.userDetails.height
    )
    const medicalSituationVisible = this.answers.insureds.every(
      (i) => i.medicalSituation && !i.medicalSituation.isEmpty()
    )
    const healthSituationVisible = this.answers.insureds.every(
      (i) => i.healthSituation && !i.healthSituation.isEmpty()
    )

    return [
      {
        label: this.t(`${i18nRoot}.items.0.label`),
        visible: bodyMeasureVisible,
        page: 'Step3_1_InsuranceBodyMeasurementsForm'
      },
      {
        label: this.t(`${i18nRoot}.items.1.label`),
        visible: medicalSituationVisible,
        page: 'Step3_2_MedicalSituationForm'
      },
      {
        label: this.t(`${i18nRoot}.items.2.label`),
        visible: healthSituationVisible,
        page: 'Step3_3_HealthConditionsForm'
      }
    ]
  }

  getProductName() {
    return this.answers.product?.title ?? ''
  }

  getPrice() {
    const product = this.answers.product
    const price = priceFormat(this.answers.price)

    const config = this.foreignHealthStore.getBranchConfiguration()
    const billingCycle = `/${config.billingCycles[this.answers.billingCycle as PricePeriodType]
      ?.textInPrice}`

    return `${price}${product?.units}${billingCycle}`
  }

  getRequestNumber() {
    return String(this.answers.product?.quotationNumber)
  }

  getStepsData(subStepIndex: number) {
    const hasCoverages = this.answers.insureds.some((i) => i.additionalCoverages.length > 0)
    const step1Page = hasCoverages ? 'Step1_2_CoveragesForm' : 'Step1_1_BetterAssuranceForm'

    const step2Page =
      this.answers.holder.role === UserRoleEnum.HOLDER_NOT_INSURED
        ? `Step2_0_2_InsuranceAnotherCompanyDataForm`
        : `Step2_0_1_InsuranceAnotherCompanyForm`

    return [
      {
        label: this.t('hiring.summary.health.step0.label'),
        type: 'TYPE1',
        items: this.#getStep0Summary(),
        page: 'Step0_1_StartDateForm'
      },
      {
        label: this.t('hiring.summary.foreign-health.step1.label'),
        type: 'TYPE2',
        items: this.#getStep1Summary(),
        page: step1Page
      },
      {
        label: this.t('hiring.summary.health.step2.label'),
        type: 'TYPE3',
        items: this.#getInsuredsData(subStepIndex),
        page: step2Page
      },
      {
        label: this.t('hiring.summary.health.step3.label'),
        type: 'TYPE3',
        items: this.#getStep3Summary(),
        page: 'Step3_1_InsuranceBodyMeasurementsForm'
      }
    ]
  }
}
